import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { 
  ResolutionType, 
  RESOLUTION_TYPE_LABELS,
  ProcurementDiscrepancy 
} from '@/types/database/procurement-discrepancies';
import { formatCurrency } from '@/lib/utils';
import { AlertTriangle, ArrowUp, ArrowDown } from 'lucide-react';

interface DiscrepancyResolutionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  discrepancy: ProcurementDiscrepancy | null;
  onResolve: (resolutionType: ResolutionType, notes: string) => Promise<void>;
  isSubmitting?: boolean;
}

const DiscrepancyResolutionDialog: React.FC<DiscrepancyResolutionDialogProps> = ({
  open,
  onOpenChange,
  discrepancy,
  onResolve,
  isSubmitting = false,
}) => {
  const [resolutionType, setResolutionType] = useState<ResolutionType | ''>('');
  const [notes, setNotes] = useState('');

  const handleSubmit = async () => {
    if (!resolutionType) return;
    await onResolve(resolutionType, notes);
    setResolutionType('');
    setNotes('');
  };

  const handleClose = () => {
    setResolutionType('');
    setNotes('');
    onOpenChange(false);
  };

  if (!discrepancy) return null;

  const isOverpayment = discrepancy.discrepancy_type === 'overpayment';

  // Filter resolution types based on discrepancy type
  const availableResolutions: ResolutionType[] = isOverpayment
    ? ['refund_received', 'credit_note', 'waived', 'other']
    : ['additional_payment', 'waived', 'other'];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Resolve Payment Discrepancy
          </DialogTitle>
          <DialogDescription>
            Record how this discrepancy was handled for audit purposes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Summary */}
          <div className={`p-4 rounded-lg ${isOverpayment ? 'bg-orange-50' : 'bg-blue-50'}`}>
            <div className="flex items-center gap-2 mb-2">
              {isOverpayment ? (
                <ArrowUp className="h-4 w-4 text-orange-600" />
              ) : (
                <ArrowDown className="h-4 w-4 text-blue-600" />
              )}
              <span className={`font-medium ${isOverpayment ? 'text-orange-700' : 'text-blue-700'}`}>
                {isOverpayment ? 'Overpayment' : 'Underpayment'} of {formatCurrency(Math.abs(discrepancy.discrepancy_amount))}
              </span>
            </div>
            <div className="text-sm space-y-1 text-muted-foreground">
              <p>Originally Paid: {formatCurrency(discrepancy.original_paid_amount)}</p>
              <p>Actual Value: {formatCurrency(discrepancy.final_calculated_amount)}</p>
            </div>
          </div>

          {/* Resolution Type */}
          <div className="space-y-3">
            <Label>How was this discrepancy resolved?</Label>
            <RadioGroup
              value={resolutionType}
              onValueChange={(value) => setResolutionType(value as ResolutionType)}
            >
              {availableResolutions.map((type) => (
                <div key={type} className="flex items-center space-x-2">
                  <RadioGroupItem value={type} id={type} />
                  <Label htmlFor={type} className="cursor-pointer">
                    {RESOLUTION_TYPE_LABELS[type]}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="resolution-notes">
              Resolution Notes {resolutionType === 'waived' && <span className="text-destructive">*</span>}
            </Label>
            <Textarea
              id="resolution-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={
                resolutionType === 'waived'
                  ? 'Explain why this discrepancy is being waived...'
                  : 'Add any relevant notes about the resolution...'
              }
              rows={3}
            />
            {resolutionType === 'waived' && (
              <p className="text-xs text-muted-foreground">
                A justification is required when waiving a discrepancy.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!resolutionType || (resolutionType === 'waived' && !notes.trim()) || isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Confirm Resolution'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DiscrepancyResolutionDialog;
